import { FlySvgSprite } from '@fly/svg';
import classNames from 'classnames';

type FlyIconGroupProps = {
  icons: string[];
  size: '1' | '2' | '3' | '4';
  color?: 'gray' | 'blue';
  highContrast?: boolean;
  className?: string;
};

const itemSizeMap = {
  1: 20,
  2: 24,
  3: 32,
  4: 40,
};
const iconSizeMap = {
  1: 11.33,
  2: 14,
  3: 19.33,
  4: 24.67,
};
const textSizeMap = {
  1: 'text-size-fly-1',
  2: 'text-size-fly-1',
  3: 'text-size-fly-2',
  4: 'text-size-fly-3',
};

const colorMap = {
  gray: {
    contrast: {
      borderColor: 'var(--fly-color-scheme-gray-3)',
      bgColor: 'var(--fly-color-scheme-gray-3)',
    },
    default: {
      borderColor: 'var(--fly-color-scheme-gray-3)',
      bgColor: 'var(--fly-color-scheme-gray-2)',
    },
  },
  blue: {
    contrast: {
      borderColor: 'var(--fly-color-scheme-blue-1)',
      bgColor: 'var(--fly-color-scheme-blue-1)',
    },
    default: {
      borderColor: 'var(--fly-color-scheme-blue-3)',
      bgColor: 'var(--fly-color-scheme-blue-3)',
    },
  },
};

const MAX_ICONS = 5;

export function FlyIconGroup({
  icons,
  className,
  size = '1',
  color = 'gray',
  highContrast = false,
  ...rest
}: FlyIconGroupProps) {
  const colorScheme = colorMap[color][highContrast ? 'contrast' : 'default'];
  const itemSize = itemSizeMap[size];

  const overflowCount =
    icons.length > MAX_ICONS ? icons.length - MAX_ICONS - 1 : 0;
  const visibleIcons =
    overflowCount > 0 ? icons.slice(0, MAX_ICONS - 1) : icons;

  return (
    <div
      {...rest}
      className={classNames('flex w-fit items-center', className)}
      style={
        {
          '--svg-border-color': colorScheme.borderColor,
          '--svg-bg-color': colorScheme.bgColor,
        } as any
      }
    >
      {visibleIcons[0] && (
        <div
          key={visibleIcons[0]}
          className={classNames(
            'relative flex items-center rounded-full border-[0.5px]',
            colorScheme
          )}
          style={{
            width: itemSize,
            height: itemSize,
            background: colorScheme.bgColor,
            borderColor: colorScheme.borderColor,
          }}
        >
          <FlySvgSprite
            name={visibleIcons[0]}
            size={iconSizeMap[size]}
            className='absolute top-[50%] left-[50%] z-10 translate-x-[-50%] translate-y-[-50%]'
          />
        </div>
      )}
      {visibleIcons.slice(1).map((icon) => (
        <div
          key={icon}
          className={classNames(
            'relative ml-[-1.5px] flex items-center',
            colorScheme
          )}
          style={{
            width: itemSize,
            height: itemSize,
          }}
        >
          <FlySvgSprite
            name='icon-group'
            size={itemSize}
            className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]'
          />
          <FlySvgSprite
            name={icon}
            size={iconSizeMap[size]}
            className='absolute top-[50%] left-[50%] z-10 translate-x-[-50%] translate-y-[-50%]'
          />
        </div>
      ))}
      {!!overflowCount && (
        <div
          className={classNames(
            'relative ml-[-1.5px] flex items-center',
            colorScheme
          )}
          style={{
            width: itemSize,
            height: itemSize,
          }}
        >
          <FlySvgSprite
            name='icon-group'
            size={itemSize}
            className='absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]'
          />
          <span
            className={classNames(
              'absolute top-[50%] left-[50%] z-10 flex translate-x-[-50%] translate-y-[-50%] items-center justify-center',
              textSizeMap[size]
            )}
          >
            +{overflowCount}
          </span>
        </div>
      )}
    </div>
  );
}
